import { defineStore } from 'pinia';
import _ from 'lodash';
import CopyPasteUtil from '@/utils/copyPaste';
import { useContentStore } from '@/stores/content';

// This module is for copy & paste of answers, and their branches

interface CopyPasteProtocolEntry {
  /** Box name of the original (source) box */
  boxOriginal: string;
  /** Box name of the newly created clone */
  boxCloned: string;
}

interface CopyPasteState {
  /** The copied answer object (with intent/channel stamped on it); null when clipboard is empty */
  answer: any | null;
  /** Mapping of original boxes to their clones during a crawl; reset after each paste */
  protocol: CopyPasteProtocolEntry[];
  /** Box names (or 'answer') currently being cloned — used to show loading state */
  loadingStack: string[];
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/copyPaste.ts` (namespace
 * `copyPaste`). In-memory only — no `vuexPersist` option (matches legacy: this namespace was
 * never listed in `src/store/index.ts`'s `vuexLocal`/`vuexSession` reducers).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy had both state prop `loadingStack` and a getter `loadingStack` that is a pure identity
 *   passthrough (`(state) => state.loadingStack`). Per the state-prop/getter collision rule, the
 *   getter is renamed `loadingStackGetter`; consumers can also just read `store.loadingStack`
 *   directly off state.
 * - Legacy mutation `content/createBox` and getter `content/getContent` are cross-namespace
 *   (root) accesses into the `content` module (legacy file `contentStore.ts`, Pinia id
 *   `'content'`) — resolved here via `useContentStore()` called inside the action bodies.
 */
export const useCopyPasteStore = defineStore('copyPaste', {
  state: (): CopyPasteState => ({
    answer: null,
    protocol: [],
    loadingStack: [],
  }),
  getters: {
    answerInClipboard: (state): any | null => state.answer,
    /**
     * Renamed from legacy getter `loadingStack` (collides with the state prop of the same name —
     * the state prop keeps the plain name). Identity passthrough kept for parity with legacy
     * call sites that used the getter form.
     */
    loadingStackGetter: (state): string[] => state.loadingStack,
  },
  actions: {
    /**
     *
     * @param {{
     *   currentBotId,
     *   intent,
     *   box,
     *   template
     * }} payload
     * @returns sring boxName
     */
    async cloneBox(payload: any): Promise<string> {
      const contentStore = useContentStore();
      const boxName = await contentStore.createBox({
        botId: payload.currentBotId,
        intent: payload.intent,
        channel: payload.channel || null,
        template: payload.template,
      });

      this.addToProtocol({
        boxOriginal: payload.box,
        boxCloned: boxName,
      });

      return boxName;
    },
    /**
     *
     * @param {{
     *   currentBotId,
     *   intent,
     *   targetIntent,
     *   box,  // starts with answer.box ->
     *   alreadyCloned,
     * }} payload
     * @returns sring boxName
     */
    async crawl(payload: any): Promise<string> {
      const contentStore = useContentStore();

      // Step 2. get box from protocol
      let boxOriginal = this.protocol.find(
        (item: CopyPasteProtocolEntry) => item.boxCloned === payload.box,
      )?.boxOriginal;

      // first crawl
      if (!boxOriginal) boxOriginal = payload.box;

      // Step 2.1 get content of answer or sub-step
      const content = contentStore.getContent(
        payload.currentBotId,
        payload.intent,
        boxOriginal!,
      );
      // Step 3. create new box
      let boxName;

      // Clone box
      if (!payload.alreadyCloned) {
        boxName = await this.cloneBox({
          currentBotId: payload.currentBotId,
          intent: payload.targetIntent,
          box: boxOriginal,
          template: content.template,
        });
      } else {
        boxName = payload.box;
      }

      // Step 4. get content of new created box
      const boxContent = contentStore.getContent(
        payload.currentBotId,
        payload.targetIntent,
        boxName,
      );

      if (content.answer)
        boxContent.answer = CopyPasteUtil.cloneAnswer(content.answer);
      if (content.answers)
        boxContent.answers = CopyPasteUtil.cloneAnswers(content.answers);
      if (content.question)
        boxContent.question = CopyPasteUtil.cloneQuestion(content.question);

      // Clone answer boxes
      if (boxContent.answers) {
        for (let a of boxContent.answers) {
          if (a.box) {
            const newBoxName = await this.cloneBox({
              currentBotId: payload.currentBotId,
              intent: payload.targetIntent,
              box: a.box,
              template: a.template,
            });

            a.box = newBoxName;
            this.addToLoadingStack(newBoxName);
            await this.crawl({
              currentBotId: payload.currentBotId,
              intent: payload.intent,
              targetIntent: payload.targetIntent,
              channel: payload.channel || null,
              box: newBoxName,
              alreadyCloned: true,
            });
            this.removeFromLoadingStack(newBoxName);
          }
        }
      }

      // Step 5. Check if box has followup. If so, template should be:
      // FollowupIntent || YesNoQuestionInfo || MultipleChoiceInfo || MultipleChoiceSlider
      if (content?.followup?.box) {
        // Step 6. Clone followup box
        const followupBoxName = await this.cloneBox({
          currentBotId: payload.currentBotId,
          intent: payload.targetIntent,
          box: content?.followup?.box,
          template: content?.followup.template,
        });

        // Step 6.1 put boxName into parent followup
        boxContent.followup = {
          box: followupBoxName,
          template: content.followup.template,
        };

        // Step 6.2 start next crawl loop with in crawl created box
        this.addToLoadingStack(followupBoxName);
        await this.crawl({
          currentBotId: payload.currentBotId,
          intent: payload.intent,
          targetIntent: payload.targetIntent,
          channel: payload.channel || null,
          box: followupBoxName,
          alreadyCloned: true,
        });
        this.removeFromLoadingStack(followupBoxName);
      } else {
        // Step 7. If no followup, check if box has followup template menu or feedback
        if (content?.followup?.template) {
          boxContent.followup.template = content.followup.template;
        }

        this.resetProtocol();
      }

      return boxName;
    },
    /**
     *
     * @param {{
     *   targetIntent,
     *   channel,
     * }} payload
     * @returns
     */
    async answerFromClipboard(payload: any) {
      if (!this.answer || !payload.currentBotId) return null;

      const newAnswer = _.cloneDeep(this.answer);
      newAnswer.qr = `${newAnswer.qr} (Kopie)`;

      if (
        newAnswer.template === 'FollowupIntent' ||
        newAnswer.template === 'AnswerInfo'
      ) {
        // Step 1. first crawl with box of answer
        this.addToLoadingStack('answer');
        newAnswer.box = await this.crawl({
          currentBotId: payload.currentBotId,
          intent: this.answer.intent,
          targetIntent: payload.targetIntent,
          channel: payload.channel || null,
          box: this.answer?.box,
        });

        this.removeFromLoadingStack('answer');
      }

      return newAnswer;
    },
    /**
     * Converted from legacy mutation `copyAnswer`.
     */
    copyAnswer(payload: { answer: any; intent: string; channel: any }) {
      this.answer = payload.answer;
      this.answer.intent = payload.intent;
      this.answer.channel = payload.channel;
    },
    /**
     * Converted from legacy mutation `addToProtocol`.
     */
    addToProtocol(payload: CopyPasteProtocolEntry) {
      this.protocol.push(payload);
    },
    /**
     * Converted from legacy mutation `resetProtocol`.
     */
    resetProtocol() {
      this.protocol = [];
    },
    /**
     * Converted from legacy mutation `addToLoadingStack`.
     */
    addToLoadingStack(box: string) {
      this.loadingStack.push(box);
    },
    /**
     * Converted from legacy mutation `removeFromLoadingStack`.
     */
    removeFromLoadingStack(box: string) {
      const index = this.loadingStack.indexOf(box);
      if (index < 0) return;
      this.loadingStack.splice(index, 1);
    },
  },
});
